import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaGasPump, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaClock, 
  FaCar, 
  FaTachometerAlt,
  FaTint,
  FaMoneyBillWave,
  FaSearch,
  FaBell,
  FaSyncAlt,
  FaExclamationTriangle,
  FaInbox,
  FaCheckCircle,
  FaReceipt
} from 'react-icons/fa';
import { MdHistory, MdPerson } from 'react-icons/md';
import '../../styles/Historico.css';
import '../../styles/CupomFiscalModal.css';
import CupomFiscalModal from '../../components/CupomFiscalModal';

const getStatusClass = (status) => {
  const normalizedStatus = String(status ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const statusClasses = {
    solicitado: 'status-solicitado',
    abastecido: 'status-abastecido',
    'aguardando validacao': 'status-aguardando-validacao',
    divergente: 'status-divergente',
  };

  return statusClasses[normalizedStatus] || 'status-desconhecido';
};

// Componente de item do histórico
const ItemHistorico = ({ item, onVerComprovante }) => {
  return (
    <div className="historico-card-web">
      <div className="card-header-web">
        <div className="posto-info-web">
          <FaMapMarkerAlt className="posto-icon-web" />
          <div>
            <h4 className="posto-nome-web">{item.posto_dados?.nome || item.posto || '-'}</h4>
            <div className="data-hora-web">
              <span><FaCalendarAlt /> {item.data_formatada}</span>
              <span><FaClock /> {item.hora_formatada}</span>
            </div>
          </div>
        </div>
        <div className={`status-badge-web ${getStatusClass(item.status)}`}>
          <FaCheckCircle />
          <span>{item.status}</span>
        </div>
      </div>

      <div className="card-details-web">
        <div className="detail-group">
          <div className="detail-item-web">
            <FaCar className="detail-icon" />
            <div>
              <span className="detail-label">Veículo</span>
              <span className="detail-value">{item.placa || '-'}</span>
            </div>
          </div>
          {/*
          <div className="detail-item-web">
            <FaTachometerAlt className="detail-icon" />
            <div>
              <span className="detail-label">Quilometragem</span>
              <span className="detail-value">{item.km_formatado ? `${item.km.toLocaleString()} km` : '-'}</span>
            </div>
          </div>
          */}
        </div>

        <div className="detail-group">
          <div className="detail-item-web">
            <FaTint className="detail-icon" />
            <div>
              <span className="detail-label">Combustível</span>
              <span className="detail-value">{item.combustivel || '-'}</span>
            </div>
          </div>
          <div className="detail-item-web">
            <FaGasPump className="detail-icon" />
            <div>
              <span className="detail-label">Litros</span>
              <span className="detail-value">{item.litros_formatado ? `${item.litros_formatado} L` : '-'}</span>
            </div>
          </div>
          <div className="detail-item-web">
            <FaMoneyBillWave className="detail-icon" />
            <div>
              <span className="detail-label">Valor Total</span>
              <span className="detail-value highlight">{item.total ? `R$ ${item.total_formatado}` : '-'}</span>
            </div>
          </div>
        </div>
            <div className="card-actions-web">
              <button className="btn-comprovante-web" onClick={() => onVerComprovante(item)}>
                <FaReceipt />
                <span>Ver Comprovante</span>
              </button>
            </div>
      </div>
    </div>
  );
};

// Componente principal
const HistoricoScreen = () => {
  const navigate = useNavigate();
  const { user, apiService, tokenReady } = useAuth();
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCupomModal, setShowCupomModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const carregarHistorico = async () => {
    if (!tokenReady) return;
    
    try {
      setError(null);
      const response = await apiService.getHistorico({ pagina: 0, limite: 50 });
      
      if (response.sucesso && response.historico) {
        setHistorico(response.historico);
      } else {
        setError(response.mensagem || 'Erro ao carregar histórico');
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setError('Erro ao carregar histórico');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (tokenReady) {
      carregarHistorico();
    }
  }, [tokenReady]);

  const onRefresh = () => {
    setRefreshing(true);
    carregarHistorico();
  };

  const handleVerComprovante = (item) => {
    console.log('Historico - Clicou em Ver Comprovante, item:', item);
    setSelectedItem(item);
    setShowCupomModal(true);
  };

  const handleFecharCupom = () => {
    setShowCupomModal(false);
    setSelectedItem(null);
  };

  const handleImprimirCupom = () => {
    window.print();
  };

  // Filtrar histórico por busca
  const filteredHistorico = historico.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.posto_dados?.nome || item.posto || '').toLowerCase().includes(term) ||
      (item.placa || '').toLowerCase().includes(term) ||
      (item.combustivel || '').toLowerCase().includes(term)
    );
  });

  // Estatísticas
  const stats = {
    total: historico.length,
    totalLitros: historico.reduce((sum, item) => sum + (parseFloat(item.litros) || 0), 0).toFixed(1),
    totalGasto: historico.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toFixed(2),
  };

  return (
    <div className="historico-web">
      {/* Header da página */}
      <div className="page-header">
        <div className="header-title">
          <MdHistory className="header-icon" />
          <h1>Histórico de Abastecimentos</h1>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Abastecimentos</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.totalLitros}L</span>
            <span className="stat-label">Total Litros</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">R$ {stats.totalGasto}</span>
            <span className="stat-label">Total Gasto</span>
          </div>
        </div>
      </div>

      {/* Barra de busca */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por posto, placa ou combustível..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          className={`refresh-btn-web ${refreshing ? 'loading' : ''}`}
          onClick={onRefresh}
          disabled={refreshing}
        >
          <FaSyncAlt className={refreshing ? 'spin' : ''} />
          <span>{refreshing ? 'Atualizando...' : 'Atualizar'}</span>
        </button>
      </div>

      {/* Conteúdo principal */}
      {loading ? (
        <div className="loading-state-web">
          <div className="loading-spinner-web"></div>
          <p>Carregando histórico...</p>
        </div>
      ) : error ? (
        <div className="error-state-web">
          <FaExclamationTriangle className="error-icon" />
          <h3>Erro ao carregar</h3>
          <p>{error}</p>
          <button className="retry-btn-web" onClick={carregarHistorico}>
            Tentar novamente
          </button>
        </div>
      ) : filteredHistorico.length === 0 ? (
        <div className="empty-state-web">
          <FaInbox className="empty-icon" />
          <h3>Nenhum abastecimento encontrado</h3>
          <p>
            {searchTerm 
              ? `Nenhum resultado para "${searchTerm}"` 
              : 'Você ainda não realizou nenhum abastecimento'}
          </p>
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
              Limpar busca
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="historico-stats">
            <p>
              Mostrando <strong>{filteredHistorico.length}</strong> de{' '}
              <strong>{historico.length}</strong> abastecimentos
              {searchTerm && ` para "${searchTerm}"`}
            </p>
          </div>
          
          <div className="historico-grid">
            {filteredHistorico.map((item) => (
              <ItemHistorico key={item.id} item={item} onVerComprovante={handleVerComprovante} />
            ))}
          </div>
        </>
      )}
      <CupomFiscalModal
        show={showCupomModal}
        onClose={handleFecharCupom}
        onPrint={handleImprimirCupom}
        dados={{
          litros: selectedItem?.litros,
          solicitacao: selectedItem,
          posto: selectedItem?.posto_dados,
          precoLitro: selectedItem?.total / selectedItem?.litros,
          user: user
        }}
      />
    </div>
  );
};

export default HistoricoScreen;